import type { Survey } from "@/features/survey/types";
import { useGetLeaseCompsQuery } from "@/services/leaseCompsService";
import { EmeraldIcon } from "@emerald-react/icon";
import { EmeraldMenu } from "@emerald-react/menu";
import { useState } from "react";
import { Link } from "react-router";

interface MarketRowProps {
  survey: Survey;
  isActive: boolean;
  onNavigate: () => void;
}

// Each row fetches its own comps count so the list can render any number of markets.
const MarketRow = ({ survey, isActive, onNavigate }: MarketRowProps) => {
  const { data: leaseComps } = useGetLeaseCompsQuery(survey.id);

  const propertyCount =
    survey.properties?.filter((property) => !property.isHidden).length ?? 0;
  const compCount = leaseComps?.filter((comp) => !comp.isHidden).length ?? 0;

  return (
    <Link
      to={`/interactive/${survey.id}/marketmap`}
      onClick={onNavigate}
      className={`flex items-center justify-between gap-4 px-[18px] py-[11px] text-white hover:bg-white/[0.07] transition-colors ${
        isActive ? "bg-white/[0.07]" : ""
      }`}
    >
      <span className="text-[15px] whitespace-nowrap">{survey.name}</span>
      <span className="text-[13px] text-[#a9bdba] whitespace-nowrap">
        {propertyCount} {propertyCount === 1 ? "property" : "properties"},{" "}
        {compCount} {compCount === 1 ? "comp" : "comps"}
      </span>
    </Link>
  );
};

interface MarketsMenuListProps {
  surveys: Survey[];
  activeSurveyId?: string;
  onNavigate: () => void;
}

// Shared panel content so other triggers (e.g. the market switcher) can open the same list.
export const MarketsMenuList = ({ surveys, activeSurveyId, onNavigate }: MarketsMenuListProps) => (
  <div className="min-w-[300px] shadow-[0_12px_32px_rgba(0,0,0,.35)] bg-[#17393d] py-1.5">
    {surveys.map((survey) => (
      <MarketRow
        key={survey.id}
        survey={survey}
        isActive={survey.id === activeSurveyId}
        onNavigate={onNavigate}
      />
    ))}
  </div>
);

interface MarketsDropdownProps {
  surveys: Survey[];
  activeSurveyId?: string;
  isActive?: boolean;
}

/**
 * "Markets (N)" nav dropdown listing each market with its property/comp
 * counts; selecting one navigates to that survey's Market Map. Used on both
 * the group cover page and the per-market tab strip.
 */
const MarketsDropdown = ({ surveys, activeSurveyId, isActive }: MarketsDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);

  if (surveys.length === 0) return null;

  return (
    <EmeraldMenu
      position="bottom_start"
      className="markets-menu"
      opened={isOpen}
      onClose={() => setIsOpen(false)}
      triggerElement={
        <div
          className={`nav-item cursor-pointer ${isActive ? "active" : ""}`}
          onClick={() => setIsOpen(!isOpen)}
        >
          {`Markets (${surveys.length})`}
          <EmeraldIcon
            icon="arrow_drop_down"
            type="outlined"
            variant="symbol"
            className="inline-block"
          />
        </div>
      }
    >
      <MarketsMenuList
        surveys={surveys}
        activeSurveyId={activeSurveyId}
        onNavigate={() => setIsOpen(false)}
      />
    </EmeraldMenu>
  );
};

export default MarketsDropdown;
